import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { adminSponsorPricingApi } from '../../api/adminSponsorPricing.api';
import { apiHandler, unwrapData } from '../../utils/apiHandler';
import { formatMoney, formatTierLabel, formatUserRange, hasFullActiveTierCoverage } from './sponsorPricingFormatters';
import '../subscription-plans/subscription-plans.css';
import './sponsor-pricing.css';

export default function SponsorPricingList() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [tiers, setTiers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [lookupOpen, setLookupOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    const result = await apiHandler(() => adminSponsorPricingApi.list());
    if (result.success) {
      const payload = unwrapData(result) || {};
      setTiers(payload.tiers || []);
      setMeta(payload.meta || null);
    } else {
      setError(result.message || 'Failed to load sponsor pricing tiers');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleDeactivate = async (tier) => {
    if (!window.confirm(`Deactivate ${formatTierLabel(tier.tierNumber)}?`)) return;
    const result = await apiHandler(() => adminSponsorPricingApi.deactivate(tier.id));
    if (result.success) {
      setMessage(result.message || `Deactivated ${formatTierLabel(tier.tierNumber)}`);
      load();
    } else {
      setError(result.message || 'Failed to deactivate tier');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!window.confirm('Replace all sponsor pricing tiers with data from this Excel file?')) return;

    setImporting(true);
    setError('');
    setMessage('');
    const result = await apiHandler(() => adminSponsorPricingApi.importXlsx(file));
    if (result.success) {
      const data = unwrapData(result);
      setMessage(result.message || `Imported ${data?.imported || 0} tiers`);
      load();
    } else {
      setError(result.message || 'Import failed');
    }
    setImporting(false);
  };

  const showAddTier = !hasFullActiveTierCoverage(tiers);

  return (
    <div className="sp-page">
      <PageHeader
        title="Sponsor Pricing"
        subtitle="Manage sponsor group associate pricing tiers (annual per-user fees)"
        actions={(
          <div className="sp-header-actions">
            <button type="button" className="sp-btn sp-btn-secondary" onClick={() => setLookupOpen(true)}>
              <i className="fa-solid fa-table" /> Lookup table
            </button>
            <button type="button" className="sp-btn sp-btn-secondary" onClick={handleImportClick} disabled={importing}>
              <i className="fa-solid fa-file-import" /> {importing ? 'Importing…' : 'Import Excel'}
            </button>
            {showAddTier && (
              <button type="button" className="sp-btn sp-btn-primary" onClick={() => navigate('/sponsor-pricing/new/edit')}>
                <i className="fa-solid fa-plus" /> Add tier
              </button>
            )}
          </div>
        )}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="sp-import-input"
        onChange={handleImportFile}
      />

      {meta && (
        <div className="sp-meta-bar">
          <span><strong>{meta.activeCount}</strong> active tiers</span>
          {meta.userRangeMin != null && (
            <span>User range: <strong>{meta.userRangeMin} – {meta.userRangeMax}</strong></span>
          )}
          {meta.lastImportedAt && (
            <span>Last updated: <strong>{new Date(meta.lastImportedAt).toLocaleString()}</strong></span>
          )}
        </div>
      )}

      {error && <div className="sp-alert sp-alert-danger">{error}</div>}
      {message && <div className="sp-alert sp-alert-success">{message}</div>}

      <div className="sp-table-wrap">
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table sp-plans-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Users</th>
                <th>Annual / user</th>
                <th>Model</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="sp-table-empty">Loading tiers…</td></tr>
              ) : tiers.length === 0 ? (
                <tr><td colSpan={6} className="sp-table-empty">No tiers found. Import Excel or add a tier.</td></tr>
              ) : tiers.map((tier) => (
                <tr key={tier.id}>
                  <td>{formatTierLabel(tier.tierNumber)}</td>
                  <td>{formatUserRange(tier.minAssociates, tier.maxAssociates)}</td>
                  <td>{formatMoney(tier.pricePerAssociateAnnual)}</td>
                  <td>{tier.pricingModelLabel || 'Annual Single Fee'}</td>
                  <td>
                    <span className={tier.isActive ? 'sp-status-active' : 'sp-status-inactive'}>
                      {tier.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="sp-table-actions">
                      <button type="button" className="sp-btn sp-btn-secondary sp-btn-sm" onClick={() => navigate(`/sponsor-pricing/${tier.id}/edit`)}>
                        Edit
                      </button>
                      {tier.isActive && (
                        <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => handleDeactivate(tier)}>
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {lookupOpen && (
        <div className="sp-modal-backdrop" onMouseDown={() => setLookupOpen(false)}>
          <div className="sp-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="sp-modal-header">
              <h3 style={{ margin: 0 }}>Sponsor Pricing Lookup Table</h3>
              <button type="button" className="sp-btn sp-btn-secondary sp-btn-sm" onClick={() => setLookupOpen(false)}>Close</button>
            </div>
            <div className="sp-modal-body">
              {meta?.hasInactiveTiers && (
                <p className="sp-section-hint" style={{ marginTop: 0 }}>
                  Showing active tiers only. Import Excel to restore the full table if tiers were deactivated.
                </p>
              )}
              <table className="data-table sp-plans-table">
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Min users</th>
                    <th>Max users</th>
                    <th>Annual / user</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.filter((t) => t.isActive).map((tier) => (
                    <tr key={tier.id}>
                      <td>{formatTierLabel(tier.tierNumber)}</td>
                      <td>{tier.minAssociates}</td>
                      <td>{tier.maxAssociates}</td>
                      <td>{formatMoney(tier.pricePerAssociateAnnual)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
